// Offline & device replication panel (Database Designer → "Offline").
//
// Sits beside ConnectionsPanel and SyncPanel because it is the same class of
// decision: which database this app runs against. Here the answer is "a copy of
// it, on a handheld, with no signal" — so the panel owns the master switch, the
// per-table replication filters, and a readiness check that runs the SAME lint
// publish runs (the designer must never disagree with what a bundle accepts).

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { getTables } from '@/api/designers'
import {
  getOfflineConfig,
  getOfflineReadiness,
  saveDeviceDatasets,
  setOfflineEnabled,
  type DeviceDataset,
} from '@/api/offline'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  margin: '10px 0 4px',
  color: 'var(--color-text-secondary)',
}

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
  background: 'var(--color-bg-secondary)',
}

function emptyDataset(): DeviceDataset {
  return {
    table: '',
    direction: 'down',
    filter: '',
    mode: 'full',
    watermark_column: '',
    reset_on_pull: false,
    via_object: '',
    enabled: true,
  }
}

interface Props {
  project: string
  open: boolean
  onClose: () => void
}

export default function OfflinePanel({ project, open, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [bizLogic, setBizLogic] = useState('')
  const [datasets, setDatasets] = useState<DeviceDataset[]>([])
  const [tables, setTables] = useState<string[]>([])
  const [problems, setProblems] = useState<string[]>([])
  const [ready, setReady] = useState(true)

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const [cfg, tbls] = await Promise.all([
        getOfflineConfig(project),
        getTables(project).catch(() => [] as Array<{ name: string }>),
      ])
      setEnabled(cfg.enabled)
      setBizLogic(cfg.biz_logic || '')
      setDatasets(cfg.datasets || [])
      setTables((tbls || []).map((t: { name: string }) => t.name).filter(Boolean))
      if (cfg.enabled) {
        const r = await getOfflineReadiness(project)
        setReady(r.ready)
        setProblems(r.problems || [])
      } else {
        setReady(true)
        setProblems([])
      }
    } catch (e) {
      toast.error('Could not load the offline configuration')
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { if (open) void load() }, [open, load])

  const toggle = async (next: boolean) => {
    try {
      const res = await setOfflineEnabled(project, next)
      setEnabled(res.enabled)
      if (res.biz_logic_switched_to_python) {
        setBizLogic('python')
        toast.success(
          'Business logic switched to Python — one source has to run on both ' +
          'the server and SQLite on the device.',
          { duration: 6000 },
        )
      }
      await load()
    } catch {
      toast.error('Could not change the offline setting')
    }
  }

  const patch = (i: number, next: Partial<DeviceDataset>) =>
    setDatasets(ds => ds.map((d, j) => (j === i ? { ...d, ...next } : d)))

  const save = async () => {
    setSaving(true)
    try {
      const res = await saveDeviceDatasets(project, datasets)
      if (res.issues?.length) {
        toast.error(`Saved with ${res.issues.length} problem(s) — see below`)
      } else {
        toast.success('Replication set saved')
      }
      await load()
    } catch {
      toast.error('Could not save the replication set')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Offline & device replication" size="wide">
      <div style={{ maxHeight: '70vh', overflow: 'auto', padding: 2 }}>
        <p style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginTop: 0 }}>
          Lets this app run on a handheld with no signal — a salesman pulling his
          stock in the morning, invoicing all day, syncing at night. The server
          database stays Postgres or Oracle; only the <em>device</em> runs SQLite.
        </p>

        <div style={card}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={loading}
              onChange={e => void toggle(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: '0.8825rem', color: 'var(--color-text-primary)' }}>
              This app can run offline
              <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                Turning this on switches business logic to <strong>Python</strong>{' '}
                (the only language that runs on both the server engine and the
                device&apos;s SQLite) and enables the publish-time checks.
                {bizLogic ? ` Currently: ${bizLogic}.` : ''}
              </span>
            </span>
          </label>
        </div>

        {enabled && (
          <>
            <div style={{
              ...card,
              borderColor: ready ? 'var(--color-border)' : '#e0a800',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: problems.length ? 8 : 0 }}>
                {ready
                  ? <CheckCircle2 size={16} style={{ color: '#2e7d32' }} />
                  : <AlertTriangle size={16} style={{ color: '#e0a800' }} />}
                <strong style={{ fontSize: '0.8625rem' }}>
                  {ready ? 'Ready to publish offline' : `${problems.length} constraint(s) unmet`}
                </strong>
                <button
                  onClick={() => void load()}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem' }}
                >
                  <RefreshCw size={12} /> Re-check
                </button>
              </div>
              {!!problems.length && (
                <>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Each of these would fail <em>silently</em> on the device — a
                    publish is refused until they are fixed.
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8125rem', lineHeight: 1.6 }}>
                    {problems.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: '0.8625rem' }}>
                Replication set ({datasets.length})
              </strong>
              <button
                onClick={() => setDatasets(d => [...d, emptyDataset()])}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8425rem' }}
              >
                <Plus size={13} /> Add table
              </button>
            </div>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 10px', lineHeight: 1.6 }}>
              A device gets only what its filter allows. Use{' '}
              <code>:device_user</code> (the salesman) and <code>:device_code</code>{' '}
              (the handheld) — they resolve from the request, so a device cannot
              widen its own scope. Whole master tables are neither safe nor
              practical to copy.
            </p>

            {datasets.map((d, i) => (
              <div key={i} style={{
                ...card,
                borderColor: d._issues?.length ? '#ef5350' : 'var(--color-border)',
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Table</label>
                    <input
                      style={inputStyle}
                      list={`offline-tables-${i}`}
                      value={d.table}
                      onChange={e => patch(i, { table: e.target.value })}
                      placeholder="SALESMAN_STOCK"
                    />
                    <datalist id={`offline-tables-${i}`}>
                      {tables.map(t => <option key={t} value={t} />)}
                    </datalist>
                  </div>
                  <div style={{ width: 150 }}>
                    <label style={labelStyle}>Direction</label>
                    <select
                      style={inputStyle}
                      value={d.direction}
                      onChange={e => patch(i, { direction: e.target.value as 'down' | 'up' })}
                    >
                      <option value="down">Down — to device</option>
                      <option value="up">Up — captured rows</option>
                    </select>
                  </div>
                  <button
                    onClick={() => setDatasets(ds => ds.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 6 }}
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {d.direction === 'down' ? (
                  <>
                    <label style={labelStyle}>Filter (scopes the pull to this device)</label>
                    <input
                      style={inputStyle}
                      value={d.filter || ''}
                      onChange={e => patch(i, { filter: e.target.value })}
                      placeholder="SALESMAN_CODE = :device_user"
                    />
                    {!!d._requires?.length && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        Needs: {d._requires.map(r => `:${r}`).join(', ')}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Refresh</label>
                        <select
                          style={inputStyle}
                          value={d.mode || 'full'}
                          onChange={e => patch(i, { mode: e.target.value as 'full' | 'watermark' })}
                        >
                          <option value="full">Full — refetch everything in scope</option>
                          <option value="watermark">Incremental — only what changed</option>
                        </select>
                      </div>
                      {d.mode === 'watermark' && (
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>Changed-at column</label>
                          <input
                            style={inputStyle}
                            value={d.watermark_column || ''}
                            onChange={e => patch(i, { watermark_column: e.target.value })}
                            placeholder="CHG_DATE"
                          />
                        </div>
                      )}
                    </div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: '0.8125rem' }}>
                      <input
                        type="checkbox"
                        checked={!!d.reset_on_pull}
                        onChange={e => patch(i, { reset_on_pull: e.target.checked })}
                      />
                      Clear the device&apos;s copy first (a fresh allocation each day)
                    </label>
                  </>
                ) : (
                  <>
                    <label style={labelStyle}>Replay through object</label>
                    <input
                      style={inputStyle}
                      value={d.via_object || ''}
                      onChange={e => patch(i, { via_object: e.target.value })}
                      placeholder="pos_invoice"
                    />
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                      Captured rows are <strong>replayed</strong> through this
                      object&apos;s validations and cross-updates on arrival — the
                      server re-derives stock rather than trusting what the device
                      computed. That is what keeps the server the system of record.
                    </div>
                  </>
                )}

                {!!d._issues?.length && (
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)', lineHeight: 1.5 }}>
                    {d._issues.map((s, j) => <li key={j}>{s}</li>)}
                  </ul>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                onClick={() => void save()}
                disabled={saving}
                style={{
                  padding: '7px 16px', fontSize: '0.8625rem', borderRadius: 6,
                  border: 'none', cursor: saving ? 'default' : 'pointer',
                  background: 'var(--color-accent)', color: '#fff',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save replication set'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
